import { cartClient } from '../common/cartClient';
import { normalizeCartResponse } from './utils/normalizeCartResponse';
import type {
  AddCartItemsRequest,
  AddCartItemsResponse,
  AddToCartRequest,
  AddToCartResponse,
  ApplyCouponRequest,
  ApplyCouponResponse,
  DeleteCartRequest,
  DeleteCartResponse,
  GetCartRequest,
  GetCartResponse,
  GetCartRewardProductsRequest,
  GetCartRewardProductsResponse,
  GetExpectedCreditRequest,
  GetExpectedCreditResponse,
  RemoveCouponRequest,
  RemoveCouponResponse,
  UpdateCartItemRequest,
  UpdateCartItemResponse,
} from './utils/types';

const REWARD_PRODUCTS_PAGE_SIZE = 40;

class CartAPIClient {
  public async getRewardProducts(
    params: GetCartRewardProductsRequest,
    options?: { signal?: AbortSignal },
  ): Promise<GetCartRewardProductsResponse> {
    return cartClient
      .get('/cart/rewardProducts', {
        params: { ...params, limit: REWARD_PRODUCTS_PAGE_SIZE },
        signal: options?.signal,
      })
      .then((res) => res.data);
  }

  public async getCart(params?: GetCartRequest, options?: { signal?: AbortSignal }): Promise<GetCartResponse> {
    return await cartClient
      .get('/cart', { params, signal: options?.signal })
      .then((res) => normalizeCartResponse(res.data));
  }

  public async addToCart(payload: AddToCartRequest): Promise<AddToCartResponse> {
    return await cartClient.post('/cart/add', payload).then((res) => normalizeCartResponse(res.data));
  }

  public async addItemsToCart(payload: AddCartItemsRequest): Promise<AddCartItemsResponse> {
    return await cartClient.post('/cart/items', payload).then((res) => normalizeCartResponse(res.data));
  }

  public async updateCartItem({ itemKey, ...payload }: UpdateCartItemRequest): Promise<UpdateCartItemResponse> {
    return await cartClient.put(`/cart/items/${itemKey}`, payload).then((res) => normalizeCartResponse(res.data));
  }

  public async deleteCartItem({ itemKey, ...payload }: DeleteCartRequest): Promise<DeleteCartResponse> {
    return await cartClient
      .delete(`/cart/items/${itemKey}`, { data: payload })
      .then((res) => normalizeCartResponse(res.data));
  }

  public async applyCoupon(payload: ApplyCouponRequest): Promise<ApplyCouponResponse> {
    return await cartClient.post('/cart/applyCoupon', payload).then((res) => normalizeCartResponse(res.data));
  }

  public async removeCoupon(payload: RemoveCouponRequest): Promise<RemoveCouponResponse> {
    return await cartClient
      .delete('/cart/removeCoupon', { data: payload })
      .then((res) => normalizeCartResponse(res.data));
  }

  /**
   * Read-only preview of the credit the authoritative cart would earn. The server reads the cart
   * from the session and the `cart-token` header, so the body carries only the two inputs it accepts.
   */
  public async getExpectedCredit({
    currency,
    appliedCreditAmount,
  }: GetExpectedCreditRequest): Promise<GetExpectedCreditResponse> {
    return await cartClient
      .post('/cart/expectedCredit', {
        currency,
        ...(appliedCreditAmount === undefined ? {} : { appliedCreditAmount }),
      })
      .then((res) => res.data);
  }
}

export const cartAPIClient = new CartAPIClient();
